<template>
  <nav :class="['sidenav', { collapsed: isCollapsed }]" aria-label="Main navigation">
    <br />
    <div class="links">
      <div class="row sidenav-brand">
        <div v-if="!isCollapsed" class="col-lg-10">
          <img
            src="../assets/images/logo.png"
            class="col-12"
            alt="Screenkode"
          />
        </div>
        <div v-else class="col-lg-12">
          <!-- Collapsed: the K mark IS the expand toggle. It crossfades into
               the » chevrons on hover/focus, then clicking reopens the rail. -->
          <button
            type="button"
            class="sidenav-logo-toggle"
            @click="toggleSidenav"
            aria-label="Expand sidebar"
            title="Expand"
          >
            <img
              src="../assets/images/logo-icon.png"
              class="sidenav-logo-icon sidenav-logo-toggle__logo"
              alt=""
              aria-hidden="true"
            />
            <!-- Crisp vector chevrons (»): stay sharp at any scale, unlike a
                 raster icon, which blurs when the spring animation overshoots. -->
            <svg
              class="sidenav-logo-toggle__expand"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.4"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <polyline points="6 5 13 12 6 19" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>
        <button
          v-if="!isCollapsed"
          type="button"
          class="toggle-btn"
          @click="toggleSidenav"
          aria-label="Collapse sidebar"
          title="Collapse"
        >
          <img src="../assets/images/icons/sidenav-collapse.png" alt="" aria-hidden="true" />
        </button>
      </div>
      <hr />
      <ul class="sidenav-list">
        <li v-for="item in navItems" :key="item.label">
          <router-link
            v-if="item.route"
            :to="{ name: item.route }"
            :class="['nav-items', { selected: isSelected(item) }]"
            :aria-current="isSelected(item) ? 'page' : null"
            :title="isCollapsed ? item.label : null"
          >
            <span class="router-link">
              <img :src="item.icon" class="sidenav-icon" alt="" aria-hidden="true" />
              <!-- Tickets and chats with unread messages: a count beside the
                   label, and a dot on the icon once the rail is retracted and
                   the label has folded away. -->
              <span v-if="badgeOf(item)" class="sidenav-icon-dot" aria-hidden="true"></span>
              <span class="d-none d-lg-block">
                {{ item.label }}
                <span v-if="badgeOf(item)" class="tk-badge sidenav-badge" :title="`${badgeOf(item)} unread`">{{ badgeLabel(badgeOf(item)) }}</span>
              </span>
            </span>
          </router-link>
          <!-- Planned destinations stay visible, but read as not-yet-available
               rather than as a link that goes nowhere. -->
          <span
            v-else
            class="nav-items is-disabled"
            aria-disabled="true"
            :title="`${item.label} — coming soon`"
          >
            <span class="router-link">
              <img :src="item.icon" class="sidenav-icon" alt="" aria-hidden="true" />
              <span class="d-none d-lg-block">
                {{ item.label }}
                <span class="new-feature-badge">Soon</span>
              </span>
            </span>
          </span>
        </li>
      </ul>
    </div>
    <div class="sidenav-footer">
      <ul class="sidenav-bottom-nav">
        <li>
          <button
            type="button"
            class="nav-items signout-nav-item"
            :title="isCollapsed ? 'Signout' : null"
            @click="logout"
          >
            <span class="router-link">
              <img
                src="../assets/images/icons/sidenav/Logout.svg"
                class="sidenav-icon"
                alt=""
                aria-hidden="true"
              />
              <span class="d-none d-lg-block signout-nav-text">Signout</span>
            </span>
          </button>
        </li>
      </ul>
    </div>
  </nav>
</template>
<script>
import {
  DASHBOARD_ROUTE,
  ORGANIZATIONS_ROUTE,
  NEW_ORGANIZATION_ROUTE,
  EDIT_ORGANIZATION_ROUTE,
  ORGANIZATION_DETAILS_ROUTE,
  ORGANIZATION_SCREENS_ROUTE,
  NEW_SCREEN_ROUTE,
  ORGANIZATION_USERS_ROUTE,
  TICKETS_ROUTE,
  CHAT_ROUTE,
  KODE_AI_ROUTE,
} from '@/router/routes';
import { LOGOUT, UPDATE_SIDENAV } from '@/store/action_types';
import { chatUnread } from '@/utils/chat_notifications';

import dashboardIcon from '@/assets/images/icons/sidenav/dashboard.svg';
import organizationsIcon from '@/assets/images/icons/sidenav/Organizations.svg';
import addOrganizationIcon from '@/assets/images/icons/sidenav/Add_Organization.svg';
import ticketsIcon from '@/assets/images/icons/sidenav/Tickets.svg';
import chatIcon from '@/assets/images/icons/sidenav/Chat.svg';
import alertsIcon from '@/assets/images/icons/sidenav/Alerts.svg';
import kodeAiIcon from '@/assets/images/icons/sidenav/Kode_AI.svg';

export default {
  data() {
    return {
      // `match` lists every route that lights up the row, so an organization's
      // sub-pages keep "Organizations" active. No `route` means the row is a
      // planned destination that is not available yet.
      navItems: [
        { label: 'Dashboard', route: DASHBOARD_ROUTE, icon: dashboardIcon, match: [DASHBOARD_ROUTE] },
        {
          label: 'Organizations',
          route: ORGANIZATIONS_ROUTE,
          icon: organizationsIcon,
          match: [
            ORGANIZATIONS_ROUTE,
            EDIT_ORGANIZATION_ROUTE,
            ORGANIZATION_DETAILS_ROUTE,
            ORGANIZATION_SCREENS_ROUTE,
            NEW_SCREEN_ROUTE,
            ORGANIZATION_USERS_ROUTE,
          ],
        },
        {
          label: 'New Organization',
          route: NEW_ORGANIZATION_ROUTE,
          icon: addOrganizationIcon,
          match: [NEW_ORGANIZATION_ROUTE],
        },
        { label: 'Chat', route: CHAT_ROUTE, icon: chatIcon, match: [CHAT_ROUTE], badge: 'chat' },
        { label: 'Tickets', route: TICKETS_ROUTE, icon: ticketsIcon, match: [TICKETS_ROUTE], badge: 'tickets' },
        { label: 'KodeAI', route: KODE_AI_ROUTE, icon: kodeAiIcon, match: [KODE_AI_ROUTE] },
        { label: 'Alerts', route: null, icon: alertsIcon, match: [] },
      ],
    };
  },
  computed: {
    route() {
      return this.$route.name;
    },
    isCollapsed() {
      return this.$store.getters.isCollapsed;
    },
    // Tickets with unread messages — the badge on the Tickets row
    // (utils/ticket_notifications keeps it current).
    unreadTickets() {
      return Number(this.$store.getters.ticketUnread?.data?.tickets) || 0;
    },
    // Conversations with unread messages — the badge on the Chat row
    // (utils/chat_notifications keeps it current).
    unreadChats() {
      return chatUnread.conversations;
    },
  },
  methods: {
    isSelected(item) {
      return item.match.includes(this.route);
    },
    badgeOf(item) {
      if (item.badge === 'tickets') return this.unreadTickets;
      if (item.badge === 'chat') return this.unreadChats;
      return 0;
    },
    badgeLabel(count) {
      return count > 99 ? '99+' : String(count);
    },
    toggleSidenav() {
      this.$store.dispatch(UPDATE_SIDENAV, !this.isCollapsed);
    },
    logout() {
      this.$store.dispatch(LOGOUT, { recordSignOut: true }).then(() => {
        this.$router.go();
      });
    },
  },
};
</script>
<style scoped>
/* =========================================================================
   Rail geometry — the icon column is the anchor. (Ported from the frontend.)

   Every horizontal offset below is a fixed px length shared by BOTH states, so
   the icons sit at exactly the same x whether the rail is open or shut.
   Retracting only narrows the rail and collapses the labels out from behind
   the icons; the icons themselves never move, resize or re-centre.

   Nothing here may be a percentage (a % offset moves with the rail width),
   and the icon may not change size between states (resizing a left-aligned
   box moves its centre).

   The component is dropped into a bootstrap `.row`, which pulls its children
   15px left of the visible area. `--sk-rail-lead` is padding that exists
   purely to cancel that out, so the sidenav's *visible* left edge is the start
   of its content box — in both states, at every breakpoint.

     |<-- lead -->|<- mx ->|<- pad ->[ icon ]<- gap ->[ label … ]
     ^ border box  ^ pill    ^ 20px slot
                  ^ visible edge (x = 0)

   `--sk-item-pad` is derived rather than typed in: it is whatever centres the
   icon slot inside the collapsed pill, which is also what centres it in the
   collapsed rail.
   ========================================================================= */
.sidenav {
  --sk-rail-lead: 15px;      /* cancels the bootstrap .row negative margin */
  /* Border-box width of the retracted rail. Owned by theme.css because every
     page's `.collapsed-col` subtracts the same token to claim the rest of the
     row — the two must stay in lockstep. Never hardcode it here. */
  --sk-rail-collapsed: var(--sidenav-collapsed-width, 63px);
  /* Usable width once the lead padding and the 1px right border are removed. */
  --sk-rail-inner: calc(var(--sk-rail-collapsed) - var(--sk-rail-lead) - 1px);
  --sk-item-mx: 8px;         /* pill gutter, identical in both states */
  --sk-icon-slot: 20px;      /* layout box every icon occupies */
  --sk-icon-gap: 12px;       /* icon -> label; only exists while expanded */
  --sk-header-h: 50px;       /* brand row, pinned so the nav below cannot shift */
  --sk-item-pad: calc(
    (var(--sk-rail-inner) - 2 * var(--sk-item-mx) - var(--sk-icon-slot)) / 2
  );
  /* Left edge of the icon column, measured from the visible edge of the rail. */
  --sk-icon-left: calc(var(--sk-item-mx) + var(--sk-item-pad));

  display: flex;
  flex-direction: column;
  height: 100vh;
  padding-left: var(--sk-rail-lead);
  padding-right: 0px;
  padding-bottom: 3px;
  background-color: var(--color-bg-canvas) !important;
  border-right: 1px solid var(--color-border);
  /* Only the rail's width animates. Its padding is state-independent — that
     is precisely what keeps the icon column still. */
  transition: width 0.3s ease;
}

.sidenav.collapsed {
  width: var(--sk-rail-collapsed);
  /* Guards against the flex row squeezing the rail below its designed width. */
  min-width: var(--sk-rail-collapsed);
}

/* The mark is 37px natural, but .col-12 would stretch it to the column width
   and upscale it. Pin it instead so it stays sharp. */
.sidenav.collapsed .sidenav-logo-icon {
  width: 34px;
  height: auto;
  max-width: 100%;
  padding: 0;
  margin: 0 auto;
  display: block;
}

/* -------------------------------------------------------------------------
   Collapsed logo doubles as the expand toggle: the K mark and the » chevrons
   share one centre; on hover/focus the mark shrinks and fades out while the
   chevrons spring in.
   ------------------------------------------------------------------------- */
.sidenav-logo-toggle {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin: 0 auto;
  padding: 0;
  border: none;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.25s ease;
}

.sidenav-logo-toggle:hover,
.sidenav-logo-toggle:focus-visible {
  background: var(--color-bg-muted, #f3f4f6);
}

.sidenav-logo-toggle:focus {
  outline: none;
}

.sidenav-logo-toggle:focus-visible {
  outline: 2px solid var(--color-primary) !important;
  outline-offset: 2px;
}

.sidenav.collapsed .sidenav-logo-toggle__logo,
.sidenav-logo-toggle__expand {
  position: absolute;
  top: 50%;
  left: 50%;
  margin: 0;
  padding: 0;
  transition: opacity 0.28s ease, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  will-change: opacity, transform;
}

/* K mark — shown at rest. */
.sidenav.collapsed .sidenav-logo-toggle__logo {
  width: 34px;
  height: auto;
  opacity: 1;
  transform: translate(-50%, -50%) scale(1) rotate(0deg);
}

/* » expand chevrons. */
.sidenav-logo-toggle__expand {
  width: 24px;
  height: 24px;
  color: var(--color-primary);
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.4);
}

.sidenav-logo-toggle:hover .sidenav-logo-toggle__logo,
.sidenav-logo-toggle:focus-visible .sidenav-logo-toggle__logo {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.5) rotate(-12deg);
}

.sidenav-logo-toggle:hover .sidenav-logo-toggle__expand,
.sidenav-logo-toggle:focus-visible .sidenav-logo-toggle__expand {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}

@media (prefers-reduced-motion: reduce) {
  .sidenav.collapsed .sidenav-logo-toggle__logo,
  .sidenav-logo-toggle__expand {
    transition: opacity 0.18s linear;
  }
  .sidenav.collapsed .sidenav-logo-toggle__logo,
  .sidenav-logo-toggle__expand,
  .sidenav-logo-toggle:hover .sidenav-logo-toggle__logo,
  .sidenav-logo-toggle:focus-visible .sidenav-logo-toggle__logo,
  .sidenav-logo-toggle:hover .sidenav-logo-toggle__expand,
  .sidenav-logo-toggle:focus-visible .sidenav-logo-toggle__expand {
    transform: translate(-50%, -50%);
  }
}

/* Brand row is pinned to one height in both states, its contents centred in
   it, so the divider and every icon below it stay put on collapse. */
.sidenav .links > .sidenav-brand {
  align-items: center;
  min-height: var(--sk-header-h);
  /* The collapse toggle stays on the wordmark's line instead of wrapping onto
     a second row, which would make the open header taller than the shut one. */
  flex-wrap: nowrap;
}

/* The wordmark's column gives up width to the toggle rather than wrap it. */
.sidenav .links > .sidenav-brand > .col-lg-10 {
  flex-shrink: 1;
  min-width: 0;
}

/* Wordmark starts on the icon column, so the brand and the nav read as one
   left edge. It is letterboxed into the fixed row height rather than sized by
   it, so it keeps its aspect and simply gets smaller on a narrower rail. */
.sidenav .sidenav-brand img.col-12 {
  height: var(--sk-header-h);
  padding-left: var(--sk-icon-left);
  object-fit: contain;
  object-position: left center;
}

.sidenav.collapsed .sidenav-brand {
  margin-left: 0;
  margin-right: 0;
}

.sidenav.collapsed .sidenav-brand > div {
  padding-left: 0;
  padding-right: 0;
}

.toggle-btn {
  flex: none;
  padding: 0;
  background-color: transparent;
  border: none;
  color: var(--color-primary);
  font-size: 1.2rem;
  cursor: pointer;
  margin-right: 13px;
}

.toggle-btn:focus {
  outline: none;
}

.toggle-btn:focus-visible {
  outline: 2px solid var(--color-primary) !important;
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}

/* Divider lines up with the nav pills, so in the retracted rail it reads as
   exactly as wide as the buttons under it. */
.sidenav hr {
  margin-left: var(--sk-item-mx);
  margin-right: var(--sk-item-mx);
  margin-bottom: 30px;
}

.sidenav ul {
  list-style: none;
  width: 100%;
  margin-block-start: 0px;
  padding-inline-start: 0px;
}

/* Pill box — state-independent by design. `.selected` below only repaints
   it; it must never restate geometry, or the two states could drift apart. */
.sidenav ul .nav-items {
  display: block;
  width: calc(100% - 2 * var(--sk-item-mx));
  margin-left: var(--sk-item-mx);
  margin-right: var(--sk-item-mx);
  margin-top: 4px;
  padding: 0.55rem var(--sk-item-pad);
  font-weight: 500;
  font-size: 0.9rem;
  line-height: 1.5rem;
  color: var(--color-text-secondary);
  text-decoration: none !important;
  border-radius: var(--radius-md) !important;
  transition: background-color .15s ease, color .15s ease;
}

/* The Signout row is a <button>: strip the native chrome so it is the same
   pill as its link neighbours. */
.sidenav ul button.nav-items {
  border: 0;
  background: transparent;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.sidenav ul .selected {
  font-weight: 600;
  color: var(--color-primary-hover);
  background-color: var(--color-primary-soft);
}

.sidenav ul .nav-items:hover {
  background-color: var(--color-primary-soft);
  color: var(--color-primary-hover);
}

.sidenav ul .nav-items:focus-visible {
  outline: none;
  box-shadow: var(--shadow-focus) !important;
}

/* A planned destination: visible, but inert. */
.sidenav ul .nav-items.is-disabled,
.sidenav ul .nav-items.is-disabled:hover {
  color: var(--color-text-muted);
  background-color: transparent;
  cursor: default;
}

.sidenav ul .nav-items.is-disabled .sidenav-icon {
  opacity: 0.55;
}

.sidenav ul .router-link {
  display: flex;
  align-items: center;
  /* Never centred: centring would make the icon's x a function of the
     label's width, so it would drift across rows and slide during the
     collapse. Packed to the start it is pinned to the pill's padding edge. */
  justify-content: flex-start;
}

/* Labels fold away rather than being display:none'd (which cannot animate),
   leaving the icon behind as the sidenav retracts. Anything meant to sit
   beside the text (the Soon badge) lives inside this span, or the row's slack
   would push it to the far edge. */
.sidenav ul .router-link > .d-none {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 400px; /* large enough to never cap the label when expanded */
  margin-left: var(--sk-icon-gap);
  overflow: hidden;
  white-space: nowrap;
  opacity: 1;
  transition: opacity 0.18s ease, max-width 0.3s ease, margin-left 0.3s ease;
}

/* The ONLY thing the collapsed state changes about a nav row: the label folds
   away. Collapsed to nothing rather than display:none, so it can animate, and
   it stays in the accessibility tree, which is what names these icon-only
   links. */
.sidenav.collapsed ul .router-link > .d-none {
  max-width: 0;
  margin-left: 0;
  opacity: 0;
}

.sidenav .sidenav-bottom-nav .signout-nav-item,
.sidenav .sidenav-bottom-nav .signout-nav-item:hover {
  color: #FF383C;
}

/* A destructive row takes a red wash on hover, not the brand one. */
.sidenav .sidenav-bottom-nav .signout-nav-item:hover {
  background-color: rgba(255, 56, 60, 0.08);
}

.signout-nav-text {
  color: #FF383C;
}

/* Every icon gets the same rigid slot, in both states and on every row. That
   single fact is what makes the icons a straight, immovable column. */
.sidenav-icon {
  display: block;
  width: var(--sk-icon-slot);
  height: var(--sk-icon-slot);
  flex: 0 0 var(--sk-icon-slot);
  object-fit: contain;
}

.sidenav-footer {
  width: 100%;
  margin-top: auto;
}

.sidenav-bottom-nav {
  width: 100%;
  list-style: none;
  margin-bottom: 10px;
}

.new-feature-badge {
  /* Nested in the label rather than being a flex item of the row, so it needs
     its own display. */
  display: inline-flex;
  vertical-align: middle;
  align-items: center;
  justify-content: center;
  margin-left: 7px;
  padding: 2px 7px;
  min-width: 34px;
  height: 18px;
  border: 1px solid var(--color-border-strong);
  border-radius: 5px;
  background-color: transparent;
  color: var(--color-text-muted);
  font-size: 0.52rem;
  font-weight: 600;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* ------------------------------ unread badge ----------------------------- */
/* Inside the label, like the Soon badge, so the row's slack cannot push it
   to the far edge; it folds away with the label when the rail retracts. */
.sidenav-badge {
  margin-left: 7px;
  vertical-align: middle;
}

/* The retracted rail has no label: a dot on the icon says there is
   something waiting. Positioned off the row, so the icon column itself is
   untouched. */
.sidenav ul .router-link {
  position: relative;
}

.sidenav-icon-dot {
  display: none;
  position: absolute;
  top: -3px;
  left: calc(var(--sk-icon-slot) - 5px);
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #FF383C;
  box-shadow: 0 0 0 2px var(--color-bg-canvas);
}

.sidenav.collapsed .sidenav-icon-dot {
  display: block;
}

/* NOTE: there are deliberately no breakpoint overrides for the rail's padding,
   the pill's padding or the icon size — the geometry is one set of fixed
   lengths at every width, so the icon column never moves. */
</style>
